import {
  Card,
  Col,
  Empty,
  List,
  Row,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type {
  DashboardProjectProgress,
  DashboardResponse,
} from "../api-models";
import { PageHeader } from "../components/PageHeader";
import { ProjectLink } from "../components/ProjectLink";
import { StatCard, type StatTone } from "../components/StatCard";
import { ActivityTrend } from "../components/charts/ActivityTrend";
import { CategoryBarChart } from "../components/charts/CategoryBarChart";
import { ShareBar, type ShareSegment } from "../components/charts/ShareBar";
import { ValueMeter } from "../components/charts/ValueMeter";
import {
  CHART_INK,
  CHART_ORDINAL_3,
  CHART_ORDINAL_4,
  CHART_SERIES,
  CHART_STATUS,
  fmtMVRCompact,
} from "../components/charts/chartTheme";
import { useUser } from "../context/UserContext";
import { api } from "../helpers/api";
import { DT_FORMATS } from "../helpers/constants";
import { fmtItemFields, fmtMVR, fmtQty } from "../helpers/format";
import { hasAnyGrant } from "../helpers/permissions";

interface Tile {
  key: string;
  title: string;
  value?: number;
  to?: string;
  tone?: StatTone;
  hint: string;
}

/** The BOM line states, in the order they progress. Over-consumed is an exception, not a
 *  further step, so it wears the reserved critical colour and always carries its label. */
const LINE_STATES: {
  key: keyof DashboardProjectProgress;
  label: string;
  color: string;
}[] = [
  { key: "notStarted", label: "Not started", color: CHART_ORDINAL_3[0] },
  { key: "inProgress", label: "In progress", color: CHART_ORDINAL_3[1] },
  { key: "completed", label: "Completed", color: CHART_ORDINAL_3[2] },
  { key: "overConsumed", label: "Over-consumed", color: CHART_STATUS.critical },
];

export default function DashboardPage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<DashboardResponse>("/reports/dashboard")
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  }, []);

  // The tiles mirror the server's own gates (dashboard.go) rather than reading a zero as
  // an answer: a count the caller may not see comes back as 0, and "nothing to buy" must
  // not look like "you cannot see procurement".
  const visible = data?.visible;
  // A tile links only where the user may actually go: the server would refuse the
  // drill-down anyway, and a tile that 403s is worse than one that just states its number.
  const canReports = !!visible?.reports;
  const canPOs = hasAnyGrant(user, "PO.Read");
  const canDiscrepancies = hasAnyGrant(user, "Discrepancies.Read");
  const reportLink = (path: string) => (canReports ? path : undefined);

  const focus = data?.focus;
  const projects = data?.projectProgress ?? [];

  // What is on someone's plate right now.
  const workload: Tile[] = [
    {
      key: "approvals",
      title: "Pending Approvals",
      value: data?.pendingApprovals,
      to: "/approvals",
      hint: data?.overdueApprovals
        ? `${data.overdueApprovals} past due`
        : "Waiting on you",
      tone: data?.overdueApprovals ? ("danger" as StatTone) : undefined,
    },
    ...(visible?.requests
      ? [
          {
            key: "openRequests",
            title: "Open Requests",
            value: data?.openRequests,
            to: "/requests?open=true",
            hint: "Raised, not yet fulfilled",
          },
        ]
      : []),
    ...(visible?.transfers
      ? [
          {
            key: "inTransit",
            title: "Transfers In Transit",
            value: data?.inTransitTransfers,
            to: "/transfers?status=DISPATCHED",
            hint: "Dispatched, not yet received",
          },
        ]
      : []),
    ...(visible?.procurement
      ? [
          {
            key: "openPOs",
            title: "Open Purchase Orders",
            value: data?.openPOs,
            to: canPOs ? "/procurement/pos?open=true" : undefined,
            hint: "Not yet fully received",
          },
          {
            key: "lowStock",
            title: "Low Stock Items",
            value: data?.lowStockCount,
            to: reportLink("/reports/low-stock"),
            tone: "danger" as StatTone,
            hint: "Below reorder level at Central",
          },
        ]
      : []),
  ];

  // Spec §24's exception list, plus §29.7's overdue requests: everything that should not
  // be true, each one clickable through to the rows it counted.
  const exceptions: Tile[] = [
    ...(visible?.requests
      ? [
          {
            key: "overdueRequests",
            title: "Overdue Requests",
            value: data?.overdueRequests,
            to: "/requests?overdue=true",
            tone: "danger" as StatTone,
            hint: "Needed-by date has passed",
          },
        ]
      : []),
    ...(visible?.exceptions
      ? [
          {
            key: "overdueTransit",
            title: "Overdue Transit",
            value: data?.overdueTransitCount,
            to: reportLink("/reports/overdue-transit"),
            tone: "danger" as StatTone,
            hint: "In transit more than 3 days",
          },
          {
            key: "discrepancies",
            title: "Open Discrepancies",
            value: data?.openDiscrepancyCount,
            to: canDiscrepancies ? "/discrepancies?status=OPEN" : undefined,
            tone: "warning" as StatTone,
            hint: "Receipt shortfalls not accounted for",
          },
          {
            key: "negativeRisk",
            title: "Negative-risk Items",
            value: data?.negativeRiskItemCount,
            to: reportLink("/reports/negative-risk-allocations"),
            tone: "danger" as StatTone,
            hint: "Allocated beyond available CW stock",
          },
          {
            key: "bomExhaustion",
            title: "BOM Exhaustion",
            value: data?.bomExhaustionCount,
            to: reportLink("/reports/bom-exhaustion"),
            tone: "warning" as StatTone,
            hint: "Approved lines with nothing left to request",
          },
        ]
      : []),
    // The threshold is only meaningful once it is configured; without it there is no count
    // to show and nothing to filter the register by.
    ...(visible?.exceptions && data?.highValueThresholdLaari != null
      ? [
          {
            key: "highValue",
            title: "High-value Approvals",
            value: data.highValueCommitmentCount,
            to: canPOs
              ? `/procurement/pos?status=PENDING_APPROVAL&minValue=${data.highValueThresholdLaari}`
              : undefined,
            tone: "warning" as StatTone,
            hint: `POs awaiting approval over ${fmtMVR(data.highValueThresholdLaari)}`,
          },
        ]
      : []),
    ...(visible?.serviceRequests
      ? [
          {
            key: "overdueSRs",
            title: "Overdue Service Requests",
            value: data?.overdueServiceRequests,
            to: "/service-requests?overdue=true",
            tone: "danger" as StatTone,
            hint: "Required date has passed",
          },
        ]
      : []),
    ...(visible?.attendance
      ? [
          {
            key: "attPending",
            title: "Attendance Not Submitted",
            value: data?.attendancePendingSubmission,
            to: "/attendance?pendingSubmission=true",
            tone: "danger" as StatTone,
            hint: "Past days still with the site",
          },
          {
            key: "attExceptions",
            title: "Attendance Exceptions",
            value: data?.attendanceExceptionCount,
            to: "/attendance?hasExceptions=true",
            tone: "warning" as StatTone,
            hint: "Sheets with lines needing an explanation",
          },
        ]
      : []),
  ];

  const renderTiles = (tiles: Tile[]) => (
    <div className="glut-stat-row">
      {tiles.map((t) => (
        <StatCard
          key={t.key}
          title={t.title}
          value={t.value}
          to={t.to}
          tone={t.tone}
          hint={t.hint}
          loading={loading}
        />
      ))}
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle={
          focusSummary(data) ??
          "Every count links through to the rows behind it."
        }
      />

      {focus && !focus.orgWide && focus.projects.length > 0 && (
        <Space size={[6, 6]} wrap style={{ marginBottom: 16 }}>
          {focus.projects.map((p) => (
            <Tag key={p.id} color="green">
              <ProjectLink id={p.id} name={p.name} />
            </Tag>
          ))}
        </Space>
      )}

      <div data-tour="dashboard-workload">{renderTiles(workload)}</div>

      {exceptions.length > 0 && (
        <>
          <Typography.Title level={5}>Exceptions</Typography.Title>
          <div data-tour="dashboard-exceptions">{renderTiles(exceptions)}</div>
        </>
      )}

      {projects.length > 0 && (
        <Card
          title={focus?.orgWide ? "Project position" : "Your projects"}
          size="small"
          data-tour="dashboard-projects"
          style={{ marginBottom: 16 }}
          extra={
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              BOM lines and value at last purchase price
            </Typography.Text>
          }
        >
          <Row gutter={[16, 16]}>
            {projects.map((project) => (
              <Col
                key={project.projectId}
                xs={24}
                lg={projects.length === 1 ? 24 : 12}
              >
                <ProjectProgressCard project={project} />
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {(data?.activity?.series?.length ?? 0) > 0 && (
        <Card
          title="Activity, last 30 days"
          size="small"
          style={{ marginBottom: 16 }}
          loading={loading}
          extra={
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Documents posted per day, on one shared scale
            </Typography.Text>
          }
        >
          {data && <ActivityTrend activity={data.activity} />}
        </Card>
      )}

      <div className="glut-chart-grid">
        <ChartCard
          title="Requests by status"
          show={(data?.requestsByStatus?.length ?? 0) > 0}
        >
          <CategoryBarChart
            data={data?.requestsByStatus?.map(toBarDatum) ?? []}
            valueName="requests"
            labelWidth={150}
          />
        </ChartCard>

        <ChartCard
          title="Approvals waiting on you"
          show={(data?.approvalAgeing?.length ?? 0) > 0}
          subtitle="By how long they have waited"
        >
          <CategoryBarChart
            data={(data?.approvalAgeing ?? []).map((b, i) => ({
              key: b.key,
              label: b.label,
              value: b.count,
              // An ordered scale, so the colour steps with the band rather than naming it.
              color:
                CHART_ORDINAL_4[i] ??
                CHART_ORDINAL_4[CHART_ORDINAL_4.length - 1],
            }))}
            valueName="waiting"
            labelWidth={110}
          />
        </ChartCard>

        <ChartCard
          title="Approvals by document type"
          show={(data?.approvalsByDocType?.length ?? 0) > 0}
        >
          <CategoryBarChart
            data={data?.approvalsByDocType?.map(toBarDatum) ?? []}
            valueName="waiting"
            labelWidth={150}
          />
        </ChartCard>

        <ChartCard
          title="Open requests by site"
          show={(data?.requestsBySite?.length ?? 0) > 1}
        >
          <CategoryBarChart
            data={data?.requestsBySite?.map(toBarDatum) ?? []}
            valueName="open requests"
            labelWidth={150}
          />
        </ChartCard>

        <ChartCard
          title="Purchase orders by status"
          show={(data?.posByStatus?.length ?? 0) > 0}
          subtitle="Committed value"
        >
          <CategoryBarChart
            data={(data?.posByStatus ?? []).map((b) => ({
              key: b.key,
              label: b.label,
              value: b.valueLaari,
            }))}
            format={(v) => fmtMVRCompact(v)}
            valueName="committed"
            labelWidth={150}
          />
        </ChartCard>

        <ChartCard
          title="Top consumed items"
          show={(data?.topConsumedItems?.length ?? 0) > 0}
          subtitle="Ranked by value — quantities in different units do not compare"
        >
          {/* Without prices there is no common axis to rank cement against cable on, so
              the chart is dropped and the table carries the figures instead of drawing a
              row of zero-length bars. */}
          {hasValue(data?.topConsumedItems) && (
            <CategoryBarChart
              data={(data?.topConsumedItems ?? []).map((item) => ({
                key: String(item.itemId),
                label: item.itemName,
                value: item.valueLaari,
              }))}
              format={(v) => fmtMVRCompact(v)}
              valueName="consumed"
              labelWidth={150}
            />
          )}
          <Table
            size="small"
            pagination={false}
            dataSource={data?.topConsumedItems ?? []}
            rowKey="itemId"
            style={{ marginTop: 12 }}
            columns={[
              {
                title: "Item",
                render: (_, r) => fmtItemFields(r.itemCode, r.itemName),
              },
              {
                title: "Quantity",
                align: "right",
                render: (_, r) => fmtQty(r.qty, r.unitCode),
              },
              {
                title: "Value",
                align: "right",
                render: (_, r) => fmtMVR(r.valueLaari),
              },
            ]}
          />
        </ChartCard>

        <ChartCard
          title="Where the stock sits"
          show={(data?.stockValueByLocation?.length ?? 0) > 0}
          subtitle="On-hand value at last purchase price"
        >
          <ShareBar
            segments={(data?.stockValueByLocation ?? []).map(
              (bucket, i): ShareSegment => ({
                key: bucket.key,
                label: bucket.label,
                value: bucket.valueLaari,
                display: fmtMVR(bucket.valueLaari),
                color: CHART_SERIES[i % CHART_SERIES.length],
              }),
            )}
          />
        </ChartCard>
      </div>

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card
            title="Over-consumption"
            size="small"
            style={{ marginBottom: 16 }}
            extra={
              canReports && <Link to="/reports/bom-vs-actual">Full report</Link>
            }
          >
            <List
              dataSource={data?.overConsumptionList ?? []}
              loading={loading}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Nothing consumed beyond its approved quantity."
                  />
                ),
              }}
              renderItem={(row) => (
                <List.Item>
                  <div>
                    {/* The item has no page of its own; its project's BOM-vs-actual is where
                        the overrun is explained. */}
                    {canReports ? (
                      <Link
                        to={`/reports/bom-vs-actual?projectId=${row.projectId}`}
                      >
                        <Typography.Text strong>
                          {fmtItemFields(row.itemCode, row.itemName)}
                        </Typography.Text>
                      </Link>
                    ) : (
                      <Typography.Text strong>
                        {fmtItemFields(row.itemCode, row.itemName)}
                      </Typography.Text>
                    )}
                    <div>
                      <ProjectLink id={row.projectId} name={row.projectName} />:{" "}
                      {fmtQty(row.consumedQty)} consumed vs{" "}
                      {fmtQty(row.approvedQty)} approved
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title="Recent stock movements"
            size="small"
            style={{ marginBottom: 16 }}
            extra={<Link to="/stock/ledger">Full ledger</Link>}
          >
            <Table
              size="small"
              pagination={false}
              loading={loading}
              dataSource={data?.recentMovements ?? []}
              rowKey="id"
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No movements"
                  />
                ),
              }}
              // A movement's context is the rest of that item's history at that location.
              onRow={(r) => ({
                onClick: () =>
                  navigate(
                    `/stock/ledger?itemId=${r.itemId}&locationId=${r.locationId}`,
                  ),
                style: { cursor: "pointer" },
              })}
              columns={[
                {
                  title: "Date",
                  dataIndex: "createdAt",
                  render: (v) => dayjs(v).format(DT_FORMATS.dateTime),
                },
                { title: "Code", render: (_, r) => r.item?.code ?? r.itemId },
                { title: "Item", render: (_, r) => r.item?.name },
                {
                  title: "Location",
                  render: (_, r) => r.location?.name ?? r.locationId,
                },
                // The event type lives on the txn the entry belongs to, not on the entry.
                { title: "Type", render: (_, r) => r.txn?.eventType },
                { title: "Qty", dataIndex: "qtyDelta" },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

/** "Project Manager · Harbour Expansion" — who the numbers below were assembled for. */
function focusSummary(data: DashboardResponse | null): string | undefined {
  const focus = data?.focus;
  if (!focus) return undefined;
  const roles =
    focus.roleNames.length > 0
      ? focus.roleNames.join(", ")
      : "No role assigned";
  if (focus.orgWide) return `${roles} · organisation-wide`;
  if (focus.projects.length === 0) return roles;
  const names = focus.projects.slice(0, 2).map((p) => p.name);
  const rest = focus.projects.length - names.length;
  return `${roles} · ${names.join(", ")}${rest > 0 ? ` and ${rest} more` : ""}`;
}

function toBarDatum(bucket: { key: string; label: string; count: number }) {
  return { key: bucket.key, label: bucket.label, value: bucket.count };
}

/** Whether a money-ranked series has any money in it — see §26: last purchase price is
 *  written by goods receipt, so a young system has quantities but no values yet. */
function hasValue(rows: { valueLaari: number }[] | undefined): boolean {
  return !!rows?.some((r) => r.valueLaari > 0);
}

/** A chart card that simply isn't rendered when the caller cannot see its series. */
function ChartCard({
  title,
  subtitle,
  show,
  children,
}: {
  title: string;
  subtitle?: string;
  show: boolean;
  children: React.ReactNode;
}) {
  if (!show) return null;
  return (
    <Card
      title={title}
      size="small"
      style={{ marginBottom: 16 }}
      extra={
        subtitle && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {subtitle}
          </Typography.Text>
        )
      }
    >
      {children}
    </Card>
  );
}

function ProjectProgressCard({
  project,
}: {
  project: DashboardProjectProgress;
}) {
  const segments = LINE_STATES.map(
    (state): ShareSegment => ({
      key: String(state.key),
      label: state.label,
      value: Number(project[state.key] ?? 0),
      color: state.color,
    }),
  );

  return (
    <Card
      size="small"
      type="inner"
      title={<ProjectLink id={project.projectId} name={project.projectName} />}
      extra={
        <Space size={4}>
          <Tag color={project.status === "ACTIVE" ? "green" : "default"}>
            {project.status.replaceAll("_", " ")}
          </Tag>
          {project.openRequests > 0 && (
            <Tag>{project.openRequests} open requests</Tag>
          )}
        </Space>
      }
    >
      {project.approvedValueLaari > 0 ? (
        <ValueMeter
          label="Consumed against approved BOM"
          value={project.consumedValueLaari}
          limit={project.approvedValueLaari}
          marker={{ value: project.issuedValueLaari, label: "Issued" }}
          format={fmtMVR}
        />
      ) : (
        // Value here is quantity priced at last purchase price, and that price is only
        // recorded when a goods receipt posts one. Saying so beats a meter reading 0 of 0.
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          No purchase prices recorded yet, so this BOM cannot be valued.
        </Typography.Text>
      )}
      <div style={{ marginTop: 14 }}>
        <Typography.Text style={{ fontSize: 12, color: CHART_INK.secondary }}>
          {project.totalLines} BOM lines
        </Typography.Text>
        <div style={{ marginTop: 6 }}>
          <ShareBar segments={segments} />
        </div>
      </div>
    </Card>
  );
}
